/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { publicHttpClient } from '@ahm/api-wrappers-core';
import { hasRefreshToken, shouldAttachBearer } from './auth-interceptor-shared';
import { getAuthToken } from './get-token';
import { persistWebinappToken, readWebinappToken } from './webinapp-token';

/**
 * Interceptor RIÊNG cho WEBINAPP (login bằng token trên URL từ app Ahamove).
 *
 * Vì sao tách hẳn khỏi web interceptor (install-axios-interceptor.ts):
 *  - Webinapp CHỈ có cookie `token`, KHÔNG có `refresh_token`. Refresh chain của
 *    web (/api/auth/refresh) sẽ luôn 401 VÀ xoá cookie token → logout oan.
 *  - Token webinapp chỉ "hoạt động" với BE SAU khi /api/auth/sync chạy xong. Trên
 *    thiết bị/webview chậm, call store/order có thể chạy TRƯỚC khi sync xong → BE
 *    trả 401 (đây là lý do bug "chỉ xảy ra ở một số thiết bị").
 *
 * Hành vi:
 *  - Request: gắn thẳng token từ cookie. KHÔNG gọi ensureValidToken (tránh trigger
 *    refresh chain → xoá cookie).
 *  - Response 401: re-activate session qua /api/auth/sync rồi retry 1 lần. Nếu vẫn
 *    fail → GIỮ phiên, reject (KHÔNG logout/redirect — webinapp không có web login;
 *    user mở lại từ app để lấy token mới).
 */

let installed = false;
let resyncInFlight: Promise<boolean> | null = null;

/**
 * Gọi từ new-auth-provider khi quan sát thấy token trên URL (entry từ app).
 * Persist token URL làm token CHÍNH (module var + localStorage bền + cookie
 * canonical) qua webinapp-token.ts — nguồn sự thật duy nhất cho token webinapp.
 */
export function setWebinappUrlToken(token: string | null) {
  if (token) persistWebinappToken(token);
}

/**
 * Token hiện tại của phiên webinapp: token URL/bền (readWebinappToken: mem →
 * localStorage → cookie). KHÔNG đọc cookie `token` trực tiếp (có thể nhập nhằng /
 * là token cũ của account khác → gốc bug gọi sync sai token).
 */
function currentToken(): string {
  return readWebinappToken();
}

/**
 * Re-activate session webinapp: gọi lại /api/auth/sync với token hiện tại.
 * Idempotent (BE dedupe theo token); dedup concurrent callers phía client để
 * nhiều request 401 cùng lúc chỉ kích hoạt 1 lần sync.
 */
async function resyncWebinappSession(): Promise<boolean> {
  if (resyncInFlight) return resyncInFlight;
  resyncInFlight = (async () => {
    const token = currentToken();
    if (!token) return false;
    try {
      const res = await fetch('/api/auth/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      return res.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    setTimeout(() => {
      resyncInFlight = null;
    }, 1000);
  });
  return resyncInFlight;
}

export function installWebinappAuthInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  publicHttpClient.interceptors.request.use((config) => {
    if (
      config.headers &&
      !config.headers.Authorization &&
      shouldAttachBearer(config.url)
    ) {
      const token = currentToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  publicHttpClient.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (
        status !== 401 ||
        !original ||
        original._retry ||
        !shouldAttachBearer(original.url)
      ) {
        return Promise.reject(error);
      }
      original._retry = true;

      const ok = await resyncWebinappSession();
      if (!ok) {
        // Token webinapp thực sự hết hiệu lực → giữ phiên, để call lỗi.
        return Promise.reject(error);
      }

      const token = currentToken();
      if (token && original.headers) {
        original.headers.Authorization = `Bearer ${token}`;
      }
      return publicHttpClient.request(original);
    }
  );
}

/**
 * Nhận diện session webinapp tại thời điểm bootstrap (trước khi cài interceptor).
 *
 * Nguyên tắc: cookie `refresh_token` là tín hiệu CHUẨN — chỉ web login mới có nó.
 *  1. CÓ refresh_token  → CHẮC CHẮN là web (kể cả khi còn sót cờ `isWebinapp` cũ
 *     hoặc URL dính `?webinapp`) → trả false. Tránh misroute web user → mất refresh
 *     chain (finding #1/#2).
 *  2. KHÔNG có refresh_token + có tín hiệu webinapp → webinapp:
 *     - urlToken (`?token=` entry từ app), hoặc
 *     - `?webinapp` param, hoặc
 *     - localStorage `isWebinapp` = 'true', hoặc
 *     - chỉ còn cookie `token` (đã dọn URL ở lần load trước; cờ localStorage có thể
 *       chưa kịp persist trên webview tablet/UA lạ) → vẫn nhận đúng webinapp khi
 *       reload, tránh rơi vào web interceptor → logout oan (finding #5).
 *  3. Không tín hiệu gì (ẩn danh) → false (web mặc định; sau khi OAuth login full
 *     reload sẽ có refresh_token → đúng nhánh web).
 */
export function isWebinappSession(urlToken?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  // (1) refresh_token tồn tại ⟹ web, ưu tiên tuyệt đối.
  if (hasRefreshToken()) return false;
  // (2) các tín hiệu webinapp (chỉ xét khi KHÔNG có refresh_token).
  if (urlToken) return true;
  try {
    if (new URLSearchParams(window.location.search).has('webinapp'))
      return true;
    // Key đồng bộ với WEBINAPP_STORAGE_KEY trong is-detect-webinapp-provider.tsx
    if (localStorage.getItem('isWebinapp') === 'true') return true;
  } catch {
    /* ignore */
  }
  // (2b) còn cookie token nhưng không có refresh_token = session webinapp (durable).
  if (getAuthToken()) return true;
  return false;
}
